import React, {
  useLayoutEffect, lazy, Suspense, useMemo,
} from 'react';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import cls from 'classnames';

import container from 'containers/App';
import Spinner from 'components/shared/Spinner';
import GoogleOneTapLogin from 'components/shared/GoogleOneTapLogin';

import history from 'config/history';
import useInAppMessage from 'hooks/useInAppMessage';
import { componentLoader } from 'utils/componentLoader';
import MNTNGlobalTrackingScript from 'utils/tracking/mountainPixel';
import { matchRoute } from 'utils/routeHelpers';
import { UNSUBSCRIBE, UNSUBSCRIBE_PRE_USERS } from 'utils/routes';
import { M } from 'utils/siteStyle';
import useScript from 'hooks/useScript';

const Alert = lazy(() => componentLoader(() => import(/* webpackChunkName: "Alert" */ 'components/Alert')));
const Routes = lazy(() => componentLoader(() => import(/* webpackChunkName: "Routes" */ 'components/routes/Routes')));

const App = ({
  initApp,
  isLoggedIn,
  id,
  restoreSessionFinished,
  smallHeaderMenuOpen,
  socialSignIn,
  store,
  path,
}) => {
  const appClipMeta = {
    staging: <meta name="apple-itunes-app" content="app-id=1227467689, app-clip-bundle-id=com.supremegolf.teetimesalpha.Clip" />,
    production: <meta name="apple-itunes-app" content="app-id=909762897, app-clip-bundle-id=com.supremegolf.teetimes.Clip" />,
  };

  useLayoutEffect(() => { initApp(); }, [initApp]);

  useInAppMessage({ isLoggedIn, id });

  const isGoogleOneTapDisabled = useMemo(() => {
    if (matchRoute(UNSUBSCRIBE, path) || matchRoute(UNSUBSCRIBE_PRE_USERS, path)) return true;
    if (isLoggedIn && restoreSessionFinished) return true;
    return false;
  });

  // MNTN Tracking Pixel (INSTALL ON ALL PAGES OF SG)
  useScript(MNTNGlobalTrackingScript, M.SG && ['preproduction', 'production'].includes(process.env.REACT_APP_ENV));

  const renderMetaHeader = (
    <Helmet
      defaultTitle={process.env.REACT_APP_SHORT_DESCRIPTION}
      titleTemplate={`%s | ${process.env.REACT_APP_META_TITLE}`}
    >
      <link rel="canonical" href={`${window.location.origin}${window.location.pathname}`} />
      {appClipMeta[process.env.REACT_APP_ENV] ? appClipMeta[process.env.REACT_APP_ENV] : null}
    </Helmet>
  );

  return (
    <Provider store={store}>
      {process.env.REACT_APP_SIGN_IN_GOOGLE_CLIENT_ID && (
        <GoogleOneTapLogin socialSignIn={socialSignIn} disabled={isGoogleOneTapDisabled} />
      )}
      <div className={cls({ 'fixed top-24 w-full': smallHeaderMenuOpen })} data-testid="app-wrapper">
        <ConnectedRouter history={history}>
          {renderMetaHeader}
          <Suspense fallback={<Spinner />}>
            <Routes />
            <Alert />
          </Suspense>
        </ConnectedRouter>
      </div>
    </Provider>
  );
};

App.defaultProps = {
  isLoggedIn: false,
  id: '',
  path: '',
  restoreSessionFinished: false,
  socialSignIn: null,
};

App.propTypes = {
  initApp: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool,
  id: PropTypes.string,
  path: PropTypes.string,
  restoreSessionFinished: PropTypes.bool,
  smallHeaderMenuOpen: PropTypes.bool.isRequired,
  socialSignIn: PropTypes.func,
  store: PropTypes.shape({
    dispatch: PropTypes.func.isRequired,
    getState: PropTypes.func.isRequired,
    subscribe: PropTypes.func.isRequired,
  }).isRequired,
};

export default container(App);
